import { SiAdobe } from "react-icons/si";
import HostedVerticalMedia from "../components/HostedVerticalMedia";
import ProfileImage from "../images/nathancolesjr.png";

const helpCards = [
  {
    title: "Short-form content",
    what: "Reels, clips, talking-head edits, product highlights, and platform-native content built for modern social platforms.",
    best: "Building familiarity, trust, and repeat visibility over time.",
  },
  {
    title: "Ad creative",
    what: "Paid ad videos, hooks, offer-focused edits, testimonial cuts, and product or service creatives designed for modern paid traffic.",
    best: "Faster conversion opportunities, stronger offers, and direct response campaigns.",
  },
  {
    title: "Long-form branded video",
    what: "Launch videos, brand pieces, promos, interviews, and cinematic content that gives your business a more polished presence.",
    best: "Deeper trust, stronger positioning, and higher-value brand perception.",
  },
];

const businessStats = [
  {
    value: "500K-2.5M",
    supporting: "Views every 30 days",
  },
  {
    value: "0-20,000",
    supporting: "Followers in 2 months",
    featured: true,
  },
  {
    value: "1,000+",
    supporting: "Short-form pieces posted",
  },
];

const selectedWork = [
  {
    title: "Hook-led promo content",
    results: "903K views • 111K likes • 4,000+ followers gained",
    why: "A strong first-second hook, immediate transformation, and clear visual payoff made the content easy to understand and hard to scroll past.",
    hostedVideoUrl: "/media/business/director_edit_ex.mp4",
    posterUrl: "",
    instagramUrl: "https://www.instagram.com/p/DKz3-AMRIAV/",
  },
  {
    title: "Educational short-form content",
    meta: {
      label: "Collaboration with Adobe",
      logo: "adobe",
    },
    results: "45.4K likes • 512,231 views • 3.4K followers gained",
    why: "Clear, useful, opinion-driven content built trust quickly and gave people a reason to engage with a specific topic.",
    hostedVideoUrl: "/media/business/aftereffects_vs_premiere_ex.mp4",
    posterUrl: "",
    instagramUrl: "https://www.instagram.com/p/DLz9UPfsxNo/",
  },
  {
    title: "Talking-head short-form content",
    results: "329,292 views • 251,676 accounts reached • 29,209 likes • 12,783 shares",
    why: "A clear delivery style, a strong angle, and helpful content people wanted to pass along helped drive both reach and engagement at a high level.",
    hostedVideoUrl: "/media/business/nathancolesjr_portfolio_talkinghead.mp4",
    posterUrl: "",
    instagramUrl: "",
  },
  {
    title: "Offer-driven short-form content",
    results: "14K views • 367 referrals • $614 in attributed sales",
    why: "A clear value proposition, relevant audience fit, and direct offer integration helped turn a relatively modest-view reel into measurable conversions.",
    hostedVideoUrl: "/media/business/plugin_affiliate_ex.mp4",
    posterUrl: "",
    instagramUrl: "https://www.instagram.com/p/DTG37RzAb9i/",
    compactMedia: true,
  },
];

const businessImpact = [
  {
    title: "4,000+ followers gained from a single post",
    supporting: "A strong hook and clear payoff helped turn one post into meaningful audience growth.",
  },
  {
    title: "200+ paid video inquiries generated from a single short-form video",
    supporting: "One short-form post created direct response by getting over 200 people to opt in through a form for a paid video.",
  },
  {
    title: "Content contributed to a major increase in inbound business and monthly revenue",
    supporting: "As I improved hooks, packaging, and consistency, content began driving stronger business results, not just reach.",
  },
  {
    title: "A story-led music video and month-long short-form campaign helped one artist roughly double his following",
    supporting: "This showed how consistent creative direction and supporting content can help grow attention around a release.",
  },
];

const businessHelp = [
  "Ongoing short-form content",
  "Ad creatives",
  "Launch or promo videos",
  "Founder or expert-led content",
  "Product-focused content",
  "Editing existing footage into stronger content",
];

const processSteps = [
  {
    title: "Plan",
    body: "We figure out what kind of content makes the most sense first, what it needs to accomplish, and how it should be structured.",
  },
  {
    title: "Shoot",
    body: "I capture or help direct the footage needed for short-form content, ad creative, or longer-form pieces.",
  },
  {
    title: "Edit",
    body: "I shape the content around attention, clarity, and presentation so it feels strong on modern platforms.",
  },
  {
    title: "Deliver",
    body: "You receive polished content ready to use across social media, paid ads, and brand channels.",
  },
];

function BusinessContent() {
  return (
    <main className="pageMain">
      <section className="section businessHero">
        <div className="businessHero__layout">
          <div className="businessHero__content">
            <h1>Premium creative for businesses that need stronger content.</h1>
            <p>I help businesses capture attention and turn it into real business.</p>
            <div className="businessHero__actions">
              <a className="button button--secondary" href="#selected-work">
                View Work
              </a>
              <a className="button button--primary" href="mailto:nathancolesjr@gmail.com">
                Contact
              </a>
            </div>
          </div>
          <div className="businessHero__portraitWrap" aria-hidden="true">
            <div className="businessHero__portraitAccent" />
            <img className="businessHero__portrait" src={ProfileImage} alt="" />
          </div>
        </div>
      </section>

      <section className="section businessSection businessSection--services">
        <div className="sectionHeading">
          <h2>What I help with</h2>
        </div>

        <div className="businessCardGrid">
          {helpCards.map((card) => (
            <article key={card.title} className="businessCard">
              <h3>{card.title}</h3>
              <div className="businessCard__group">
                <span className="businessCard__label">What it is</span>
                <p>{card.what}</p>
              </div>
              <div className="businessCard__divider" />
              <div className="businessCard__group">
                <span className="businessCard__label">Best for</span>
                <p className="businessCard__value">{card.best}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="section businessSection businessSection--proof">
        <div className="sectionHeading">
          <h2>Proof I understand attention</h2>
        </div>

        <div className="businessStatsGrid">
          {businessStats.map((stat) => (
            <article
              key={stat.supporting}
              className={`businessStatCard${stat.featured ? " businessStatCard--featured" : ""}`}
            >
              <span className="businessStatCard__value">{stat.value}</span>
              <span className="businessStatCard__supporting">{stat.supporting}</span>
            </article>
          ))}
        </div>

        <div className="businessSection__body businessSection__body--split">
          <p>These results come from content I planned, shot, edited, packaged, and posted on my own pages.</p>
          <p>
            That hands-on experience gives me a real understanding of hooks, pacing, presentation, and what
            keeps people watching.
          </p>
        </div>
      </section>

      {false ? (
        <section className="section businessSection businessBridge">
          <div className="sectionHeading">
            <h2>What that means for businesses</h2>
          </div>
          <div className="businessSection__body businessSection__body--split">
            <p>
              I understand how to structure content so it looks strong, holds attention, and fits the way
              people actually consume media today.
            </p>
            <p>
              I bring that same thinking into business content across short-form social, ad creative, and
              branded video.
            </p>
          </div>
        </section>
      ) : null}

      <section className="section businessSection businessSection--work" id="selected-work">
        <div className="sectionHeading">
          <h2>Selected work</h2>
          <p>
            These examples show how I use hooks, packaging, editing, and positioning to create content that
            gets attention and drives response.
          </p>
        </div>

        <p className="businessPositioning">
          I do not assume the same content works for every audience. I focus on finding strong hooks,
          packaging content clearly, and refining what performs based on the audience in front of it.
        </p>

        <div className="businessWorkGrid businessWorkGrid--proof">
          {selectedWork.map((item) => (
            <BusinessWorkItem key={item.title} item={item} />
          ))}
          <BusinessImpactPanel />
        </div>
      </section>

      {false ? (
        <section className="section businessSection businessSection--help">
          <div className="sectionHeading">
            <h2>How I can help your business</h2>
            <p>
              If your business needs help across social media, ads, or more polished brand videos, I can help
              shape the content from planning through final delivery.
            </p>
          </div>

          <div className="businessListGrid">
            {businessHelp.map((item) => (
              <article key={item} className="businessListItem">
                <span>{item}</span>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      <section className="section businessSection businessSection--process">
        <div className="sectionHeading">
          <h2>How I work</h2>
        </div>

        <div className="processGrid">
          {processSteps.map((step, index) => (
            <article key={step.title} className="processCard">
              <span className="processCard__index">0{index + 1}</span>
              <h3>{step.title}</h3>
              <p>{step.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="section businessCta">
        <div className="businessCta__card">
          <h2>Need stronger content for your business?</h2>
          <p>Let&apos;s talk through what kind of content would make the most sense first.</p>
          <div className="businessCta__actions">
            <a
              className="button button--secondary"
              href="https://instagram.com/nathancolesjr"
              target="_blank"
              rel="noreferrer"
            >
              Contact
            </a>
            <a className="button button--primary" href="mailto:nathancolesjr@gmail.com">
              Email Me
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}

function BusinessWorkItem({ item }) {
  return (
    <article className="businessWorkProofCard">
      <h3>{item.title}</h3>
      {item.meta ? (
        <p className="businessWorkProofCard__meta">
          {item.meta.logo === "adobe" ? <SiAdobe aria-hidden="true" /> : null}
          <span>{item.meta.label}</span>
        </p>
      ) : null}
      <p className="businessWorkProofCard__results">{item.results}</p>
      <p className="businessWorkProofCard__why">{item.why}</p>
      <HostedVerticalMedia
        mediaUrl={item.hostedVideoUrl}
        posterUrl={item.posterUrl}
        instagramUrl={item.instagramUrl}
        title={item.title}
        compact={item.compactMedia}
      />
    </article>
  );
}

function BusinessImpactPanel() {
  return (
    <article className="businessImpactCard">
      <h3>What content has done for my business</h3>
      <p className="businessImpactCard__intro">
        The content I post is not just for views. It has helped grow my audience, generate inbound interest,
        and bring in real business for my company.
      </p>
      <div className="businessImpactCard__grid">
        {businessImpact.map((item) => (
          <div key={item.title} className="businessImpactCard__item">
            <strong>{item.title}</strong>
            <p>{item.supporting}</p>
          </div>
        ))}
      </div>
      <p className="businessImpactCard__body">
        I use my own business as proof that stronger content can do more than look good. It can build
        attention, trust, response, and real momentum.
      </p>
    </article>
  );
}

export default BusinessContent;
